import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '../client';
import { queryKeys } from '../query-keys';
import type {
  AdminProductQueryParams,
  AdminProduct,
  AdminProductDetail,
  PaginatedResponse,
  AdminUpdateProductData,
  MergeProductsData,
  MergeProductsResult,
  ProductQualityMetrics,
} from '../types';

// ============================================================================
// API Functions
// ============================================================================

/**
 * Fetch paginated list of products for admin management.
 */
async function getAdminProducts(
  params?: AdminProductQueryParams
): Promise<PaginatedResponse<AdminProduct>> {
  const searchParams = new URLSearchParams();
  if (params?.search) searchParams.set('search', params.search);
  if (params?.source) searchParams.set('source', params.source);
  if (params?.quality) searchParams.set('quality', params.quality);
  if (params?.hasBarcode !== undefined) searchParams.set('hasBarcode', String(params.hasBarcode));
  if (params?.category) searchParams.set('category', params.category);
  if (params?.sortBy) searchParams.set('sortBy', params.sortBy);
  if (params?.sortOrder) searchParams.set('sortOrder', params.sortOrder);
  if (params?.page) searchParams.set('page', params.page.toString());
  if (params?.limit) searchParams.set('limit', params.limit.toString());

  const queryString = searchParams.toString();
  const url = `/admin/products${queryString ? `?${queryString}` : ''}`;

  return apiFetch<PaginatedResponse<AdminProduct>>(url);
}

/**
 * Fetch detailed product information for admin view.
 */
async function getAdminProductDetail(productId: string): Promise<AdminProductDetail> {
  return apiFetch<AdminProductDetail>(`/admin/products/${productId}`);
}

/**
 * Fetch product quality metrics for admin dashboard.
 */
async function getProductQualityMetrics(): Promise<ProductQualityMetrics> {
  return apiFetch<ProductQualityMetrics>('/admin/products/metrics');
}

/**
 * Update a product (admin can edit any product including OFF).
 */
async function updateAdminProduct(
  productId: string,
  data: AdminUpdateProductData
): Promise<AdminProduct> {
  return apiFetch<AdminProduct>(`/admin/products/${productId}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
}

/**
 * Delete a product (admin can delete any product including OFF).
 */
async function deleteAdminProduct(productId: string): Promise<void> {
  return apiFetch<void>(`/admin/products/${productId}`, {
    method: 'DELETE',
  });
}

/**
 * Merge duplicate products.
 */
async function mergeProducts(data: MergeProductsData): Promise<MergeProductsResult> {
  return apiFetch<MergeProductsResult>('/admin/products/merge', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

// ============================================================================
// Hooks
// ============================================================================

/**
 * Hook to fetch paginated list of products for admin management.
 * Supports search, filtering, sorting, and pagination.
 */
export function useAdminProducts(params?: AdminProductQueryParams) {
  return useQuery({
    queryKey: queryKeys.admin.products(params),
    queryFn: () => getAdminProducts(params),
    staleTime: 30 * 1000, // 30 seconds
  });
}

/**
 * Hook to fetch detailed product information.
 */
export function useAdminProductDetail(productId: string, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: queryKeys.admin.productDetail(productId),
    queryFn: () => getAdminProductDetail(productId),
    enabled: options?.enabled ?? !!productId,
    staleTime: 60 * 1000, // 1 minute
  });
}

/**
 * Hook to fetch product quality metrics for admin dashboard.
 */
export function useProductQualityMetrics() {
  return useQuery({
    queryKey: queryKeys.admin.productMetrics(),
    queryFn: getProductQualityMetrics,
    staleTime: 60 * 1000, // 1 minute
  });
}

/**
 * Hook to update a product.
 * Invalidates product list and detail queries on success.
 */
export function useAdminUpdateProduct() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ productId, data }: { productId: string; data: AdminUpdateProductData }) =>
      updateAdminProduct(productId, data),
    onSuccess: (updatedProduct, { productId }) => {
      // Update the product detail cache
      queryClient.setQueryData(
        queryKeys.admin.productDetail(productId),
        (old: AdminProductDetail | undefined) => {
          if (!old) return old;
          return { ...old, ...updatedProduct };
        }
      );

      // Invalidate all product list queries (any params)
      queryClient.invalidateQueries({ queryKey: [...queryKeys.admin.all, 'products'] });

      // Invalidate metrics if quality fields changed
      queryClient.invalidateQueries({ queryKey: queryKeys.admin.productMetrics() });
    },
  });
}

/**
 * Hook to delete a product.
 * Invalidates product list and metrics on success.
 */
export function useAdminDeleteProduct() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (productId: string) => deleteAdminProduct(productId),
    onSuccess: (_, productId) => {
      // Remove from detail cache
      queryClient.removeQueries({ queryKey: queryKeys.admin.productDetail(productId) });

      // Invalidate all product list queries (any params)
      queryClient.invalidateQueries({ queryKey: [...queryKeys.admin.all, 'products'] });

      // Invalidate metrics
      queryClient.invalidateQueries({ queryKey: queryKeys.admin.productMetrics() });
    },
  });
}

/**
 * Hook to merge duplicate products.
 * Invalidates product list and metrics on success.
 */
export function useAdminMergeProducts() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: MergeProductsData) => mergeProducts(data),
    onSuccess: (result) => {
      // Remove deleted products from detail cache
      for (const deletedId of result.deletedProductIds) {
        queryClient.removeQueries({ queryKey: queryKeys.admin.productDetail(deletedId) });
      }

      // Invalidate all product list queries (any params)
      queryClient.invalidateQueries({ queryKey: [...queryKeys.admin.all, 'products'] });

      // Invalidate metrics
      queryClient.invalidateQueries({ queryKey: queryKeys.admin.productMetrics() });
    },
  });
}
